import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Ticket, 
  Plus, 
  Clock, 
  CheckCircle,
  MessageSquare,
  TrendingUp,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import dashboardService from '../services/dashboardService';

const CustomerDashboard = () => {
  const { user } = useAuth();

  const { data: statsData, isLoading, error } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => dashboardService.getUserStats(),
    retry: 2,
    refetchOnWindowFocus: false
  });

  const stats = statsData?.data || {};

  const quickActions = [
    {
      title: 'Create New Ticket',
      description: 'Report a new issue or request',
      icon: Plus,
      href: '/tickets/new',
      color: 'bg-blue-600 hover:bg-blue-700 text-white'
    },
    {
      title: 'View All Tickets', 
      description: 'See all your tickets',
      icon: Ticket,
      href: '/tickets',
      color: 'bg-gray-100 hover:bg-gray-200 text-gray-900'
    },
    {
      title: 'Live Chat',
      description: 'Get immediate help',
      icon: MessageSquare,
      href: '/chat',
      color: 'bg-green-100 hover:bg-green-200 text-green-900'
    }
  ];

  // Handle API error display
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-900">Error Loading Dashboard</h3>
              <p className="mt-1 text-red-700">
                {error?.message || 'Failed to load dashboard statistics. Please try refreshing the page.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.name}!
            </h1>
            <p className="mt-1 text-gray-600">
              Here's an overview of your support tickets and activity.
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Last login</p>
            <p className="text-sm font-medium text-gray-900">
              {user?.last_login_at 
                ? new Date(user.last_login_at).toLocaleDateString()
                : 'First time'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <a
              key={action.href}
              href={action.href}
              className={`${action.color} p-6 rounded-lg transition-colors block hover:shadow-md`}
            >
              <div className="flex items-center">
                <Icon className="w-8 h-8 mr-4" />
                <div>
                  <h3 className="font-medium">{action.title}</h3>
                  <p className="text-sm opacity-90">{action.description}</p>
                </div>
              </div>
            </a>
          );
        })}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Open Tickets</h3>
              <p className="text-2xl font-bold text-blue-600">
                {isLoading ? '...' : stats.open_tickets || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Resolved</h3>
              <p className="text-2xl font-bold text-green-600">
                {isLoading ? '...' : (stats.resolved_tickets || stats.closed_tickets || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Pending</h3>
              <p className="text-2xl font-bold text-orange-600">
                {isLoading ? '...' : stats.pending_tickets || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Total Tickets</h3>
              <p className="text-2xl font-bold text-purple-600">
                {isLoading ? '...' : stats.total_tickets || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Tickets */}
      {stats.recent_tickets && stats.recent_tickets.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Tickets</h3>
            <a
              href="/tickets"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View All
            </a>
          </div>
          <div className="space-y-3">
            {stats.recent_tickets.map((ticket) => (
              <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{ticket.title}</h4>
                  <p className="text-xs text-gray-500 mb-1">
                    {ticket.custom_id || `Ticket #${ticket.id}`}
                  </p>
                  <p className="text-sm text-gray-600">
                    Created {new Date(ticket.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Breakdown */}
      {stats.status_breakdown && Object.keys(stats.status_breakdown).length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tickets by Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats.status_breakdown).map(([status, count]) => (
              <div key={status} className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-600 capitalize">{status.replace('_', ' ')}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <MessageSquare className="w-6 h-6 text-blue-600 mt-1" />
          <div className="ml-3">
            <h3 className="text-lg font-medium text-blue-900">Need Help?</h3>
            <p className="mt-1 text-blue-700">
              Our support team is here to help you. Create a ticket or start a live chat.
            </p>
            <div className="mt-4 flex space-x-3">
              <a
                href="/tickets/new"
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors"
              >
                Create Ticket
              </a>
              <a
                href="/chat"
                className="bg-white text-blue-600 px-4 py-2 rounded-md text-sm border border-blue-600 hover:bg-blue-50 transition-colors"
              >
                Start Chat
              </a>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  const statusColors = {
    'open': 'bg-blue-100 text-blue-800',
    'pending': 'bg-yellow-100 text-yellow-800',
    'assigned': 'bg-purple-100 text-purple-800',
    'in_progress': 'bg-orange-100 text-orange-800',
    'in-progress': 'bg-orange-100 text-orange-800',
    'resolved': 'bg-green-100 text-green-800',
    'closed': 'bg-gray-100 text-gray-800',
    'completed': 'bg-green-100 text-green-800'
  };
  return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
};

// Helper function to get priority color
const getPriorityColor = (priority) => {
  const priorityColors = {
    'critical': 'bg-red-100 text-red-800',
    'high': 'bg-orange-100 text-orange-800',
    'medium': 'bg-blue-100 text-blue-800',
    'low': 'bg-gray-100 text-gray-800'
  };
  return priorityColors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800';
};

export default CustomerDashboard;